import { AreaIndex, HexGrid, type BBox, type GeoJsonArea } from '@rw/geo';
import { qaRouteWaypoints } from '@rw/game-engine/testing';
import type { RegionDto } from '@rw/shared';
import { create } from 'zustand';
import { env } from '../lib/env';
import { useAuth } from '../stores/auth';
import { useNetwork } from '../stores/network';
import { BrowserGeoSource, type GeoSource } from '../features/running/engine/geo-source';
import { ReplayGeoSource } from '../features/running/engine/replay-source';
import { TrackingEngine, type TrackingSnapshot } from '../features/running/engine/tracking-engine';
import { RealtimeClient } from './realtime';
import { getRunStore, type RunStore } from './run-store';
import { SyncManager } from './sync';

/** Live tracking snapshot for React. */
export const useTracking = create<TrackingSnapshot>(() => ({}) as TrackingSnapshot);

export interface LoadedRegion {
  dto: RegionDto;
  grid: HexGrid;
  bbox: BBox;
  /** A cell is playable when its centre lies in the region's playable area (same rule as the server). */
  isPlayable(gridKey: string): boolean;
  /** True when the point lies inside the region's playable area. */
  contains(p: { lat: number; lng: number }): boolean;
}

const loadedRegions = new Map<string, LoadedRegion>();

/** loadRegion, remembered per region (the area index and cell cache are costly to rebuild). */
export function loadRegionCached(dto: RegionDto): LoadedRegion {
  let r = loadedRegions.get(dto.id);
  if (!r || r.dto !== dto) {
    r = loadRegion(dto);
    loadedRegions.set(dto.id, r);
  }
  return r;
}

/** The region whose playable area contains the point, if any. */
export function regionAt(regions: readonly RegionDto[], p: { lat: number; lng: number }): RegionDto | null {
  return regions.find((dto) => loadRegionCached(dto).contains(p)) ?? null;
}

export function loadRegion(dto: RegionDto): LoadedRegion {
  const grid = new HexGrid(dto.grid);
  const area = new AreaIndex(dto.boundary as GeoJsonArea);
  // Regions span a whole province (~330k cells): answer per cell, remembered, never enumerate them all.
  const known = new Map<string, boolean>();
  const isPlayable = (key: string) => {
    let v = known.get(key);
    if (v === undefined) {
      v = area.contains(grid.centerOf(key));
      if (known.size > 200_000) known.clear();
      known.set(key, v);
    }
    return v;
  };
  return { dto, grid, bbox: area.bbox, isPlayable, contains: (p) => area.contains(p) };
}

function createSource(getRegion: () => LoadedRegion | null): GeoSource {
  if (env.testGpsEnabled) {
    const cfg = localStorage.getItem('rw:test-gps');
    const region = getRegion();
    if (cfg && region) {
      const parsed = cfg === 'qa' ? {} : (JSON.parse(cfg) as { startCell?: string; speedup?: number });
      const { waypoints } = qaRouteWaypoints(region.grid, parsed.startCell);
      return new ReplayGeoSource({ waypoints, speedup: parsed.speedup ?? 25 });
    }
  }
  return new BrowserGeoSource();
}

export interface Runtime {
  store: RunStore;
  engine: TrackingEngine;
  sync: SyncManager;
  realtime: RealtimeClient;
  region: LoadedRegion | null;
  setRegion(dto: RegionDto): LoadedRegion;
}

let runtime: Promise<Runtime> | null = null;

export function getRuntime(): Promise<Runtime> {
  runtime ??= (async () => {
    const store = await getRunStore();
    const rt: Runtime = {
      store,
      region: null,
      engine: null as unknown as TrackingEngine,
      sync: null as unknown as SyncManager,
      realtime: new RealtimeClient(),
      setRegion(dto) {
        if (rt.region?.dto.id === dto.id) return rt.region;
        rt.region = loadRegionCached(dto);
        const r = rt.region;
        rt.engine.setRegion({ regionId: dto.id, grid: r.grid, isPlayable: r.isPlayable });
        return r;
      },
    };
    rt.engine = new TrackingEngine({ createSource: () => createSource(() => rt.region), store });
    rt.engine.subscribe((s) => useTracking.setState(s, true));
    rt.sync = new SyncManager({
      store,
      userId: () => useAuth.getState().user?.id ?? null,
      isOnline: () => useNetwork.getState().online,
      activeRunId: () => {
        const s = rt.engine.snapshot.state;
        return s === 'RUNNING' || s === 'PAUSED' || s === 'RESUMING' || s === 'STARTING' || s === 'FINISHING'
          ? rt.engine.snapshot.runId
          : null;
      },
    });
    return rt;
  })();
  return runtime;
}
